import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function OverridesGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Overrides"
      subtitle="Take manual control when you know something the system doesn't."
    >
      <GuideSection title="When to Use Overrides">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The rate engine does a good job of pricing based on your rules and demand signals. But
          sometimes you know something it does not. That is when overrides come in:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">A VIP group booking</strong> at a negotiated rate
            that does not follow your normal pricing
          </li>
          <li>
            <strong className="text-foreground">A maintenance closure</strong> where you need to
            stop-sell a room type for specific dates
          </li>
          <li>
            <strong className="text-foreground">A rate you promised</strong> a partner, travel
            agent, or returning guest
          </li>
          <li>
            <strong className="text-foreground">A date where you simply disagree</strong> with the
            engine's output -- perhaps local knowledge tells you demand will be different
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Overrides are the escape hatch. They are powerful, but should be used sparingly. If you
          find yourself overriding the same pattern repeatedly, that is a signal to adjust the
          underlying rule instead.
        </p>
      </GuideSection>

      <GuideSection title="How Overrides Work">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Overrides are applied at <strong className="text-foreground">stage 11 of 12</strong> in
          the rate pipeline. The engine computes the rate through all prior stages -- base rate,
          seasonal, day-of-week, demand, lead time, events, gap filling, derived offsets, clamping,
          and rounding. Then it checks for an active override.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            If an override exists, it <strong className="text-foreground">replaces the computed
            rate entirely</strong>
          </li>
          <li>
            The pipeline still runs in full -- you can always see what the rate{' '}
            <em>would have been</em> without the override
          </li>
          <li>
            Overrides show as an <strong className="text-foreground">amber highlight</strong> in
            the rate calendar, making them easy to spot
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You can set several types of override on a date:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Rate amount</strong> -- a specific nightly price
          </li>
          <li>
            <strong className="text-foreground">Stop sell</strong> -- the room cannot be booked
          </li>
          <li>
            <strong className="text-foreground">CTA (Closed to Arrival)</strong> -- no check-ins
            allowed on this date
          </li>
          <li>
            <strong className="text-foreground">CTD (Closed to Departure)</strong> -- no check-outs
            allowed on this date
          </li>
          <li>
            <strong className="text-foreground">Min/max stay</strong> -- length-of-stay
            restrictions
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Setting Overrides">
        <p className="text-sm leading-relaxed text-muted-foreground">
          There are two ways to create overrides:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">From the calendar:</strong> Click any date to open
            the day detail, then use the Edit Prices tab to enter an override rate or restriction
          </li>
          <li>
            <strong className="text-foreground">Batch overrides:</strong> Select a date range and
            room types to apply the same override across multiple dates at once -- useful for
            blocking out a maintenance week or setting a fixed event rate
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Overrides persist until you explicitly remove them. They will not be overwritten by
          recomputation -- the engine respects your manual decision. When you do remove an override,
          the rate reverts to whatever the pipeline computes on the next cycle.
        </p>
      </GuideSection>

      {/* COMBINED INVENTORY: a Same room takes no manual price of its own; one on its lead room
          reaches it with its offset. Static copy, the guides are property-agnostic. */}
      <GuideSection title="If Your Building Uses Combined Inventory">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A <strong className="text-foreground">Same room</strong> on a following listing has no manual prices of its
          own: it sells at the price of its room at the lead listing, so set the manual price there. The Same room
          follows it, with its own offset if it has one. A manual {'£'}150 on the lead listing&apos;s Double reaches a
          Same room with a +10% offset at {'£'}165. Restrictions you set on the lead room reach the Same room as they
          are.
        </p>
      </GuideSection>

      <GuideSection title="Override Warnings">
        <p className="text-sm leading-relaxed text-muted-foreground">
          If your override exceeds the room type's guardrails, the system displays a warning. For
          example, if you set an override of £160 on a room with a ceiling of £150, you will see
          "Above ceiling £150."
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          This is <strong className="text-foreground">informational, not blocking</strong>. The
          override still applies -- {brand} trusts your judgement. But it is a signal to
          double-check whether the override makes sense, or whether the guardrails need updating.
        </p>
      </GuideSection>

      <div className="space-y-3">
        <GuideTip type="do">
          Include a reason when setting overrides. Your future self will thank you when reviewing
          History and wondering why March 15th has a fixed rate of £85.
        </GuideTip>
        <GuideTip type="do">
          Review active overrides weekly. Stale overrides from months ago can silently prevent the
          engine from optimising -- a forgotten override on a peak weekend could cost you revenue.
        </GuideTip>
        <GuideTip type="dont">
          Don't use overrides as your primary pricing method. If you are overriding the same pattern
          repeatedly (e.g., always overriding Saturdays higher), adjust the day-of-week modifier
          instead. Let the rules do the work.
        </GuideTip>
        <GuideTip type="dont">
          Don't override every date for the next 3 months. That is just manual pricing with extra
          steps -- and you lose all the benefits of dynamic demand-based adjustments.
        </GuideTip>
      </div>

      <GuideRelatedLinks
        links={[
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'price-guardrails', title: 'Price Guardrails' },
          { slug: 'history', title: 'History' },
        ]}
      />
    </GuideLayout>
  )
}
